<template>
  <div>
    <b-card title="Embarkation Data" class="p-4 mx-auto mt-4" style="width: 100%;">
      <div class="legend">
        <span class="legend-item bg-success text-white">Landed (L)</span>
        <span class="legend-item bg-light-red">Landed with Delay</span>
        <span class="legend-item bg-dark-red text-white">Cancelled (C)</span>
        <span class="legend-item bg-light-grey">Active (A)</span>
        <span class="legend-item bg-dark-grey text-white">Active with Delay</span>
        <span class="legend-item bg-light-brown">Scheduled (S)</span>
        <b-button class="legend-item" @click="exportData" variant="primary">
          Export to Excel
        </b-button>
        <b-button class="legend-item" @click="exportFullData" variant="info">
          Export Full Data
        </b-button>
      </div>
      <div>

      </div>
      <b-form-group label="Select Date">
        <b-form-datepicker :value="inputDate" @input="handleDateChange"></b-form-datepicker>
      </b-form-group>
      <b-form-group label="Search">
        <b-input v-model="searchQuery"
          placeholder="Search by Booking Number, Guest Name, Flight Number or Cabin Number"></b-input>
      </b-form-group>
      <b-button @click="refreshData" variant="primary">
        <span v-if="loading" class="mr-2">
          <b-spinner small label="Loading..."></b-spinner>
        </span>
        Check Live Data
      </b-button>
      <span v-if="latestUpdatedAt">Last Updated: {{ formatDateTime(latestUpdatedAt) }}</span>
      <b-card>
        <b-card-text>
          <b>Total Guests: {{ totalGuests }}</b> | <b>Checked Guests: {{ totalCheckedGuests }}</b> | <b>OWN AIR: {{
            totalOwnAir }}</b>
        </b-card-text>
      </b-card>
      <template v-for="(flights, cruiseLine) in filteredData" :key="cruiseLine">
        <b-card no-body class="mb-3">
          <b-card-header header-tag="header" class="p-1 bg-primary text-white" role="tab">
            <b-button block v-b-toggle="`cruise-${cruiseLine}`" variant="dark" class="text-white text-centre">
              Cruise Line: {{ cruiseLine }} | Total Guests: {{ cruiseLineTotalGuests[cruiseLine] }}
            </b-button>
          </b-card-header>

          <b-collapse :id="`cruise-${cruiseLine}`" accordion="cruise-accordion" role="tabpanel" visible>
            <b-card-body>
              <template v-for="(group, key) in flights" :key="key">
                <EmbarkFlightGroup 
                  :group="group"
                  :flightKey="key"
                  :isExpanded="expandedGroups[key]"
                  @expand="$set(expandedGroups, key, true)"
                  @counts-updated="updateCheckedCount(group.data)"
                  @data-updated="fetchEmbarkations"
                />
              </template>
            </b-card-body>
          </b-collapse>
        </b-card>
      </template>

    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import { prepareAndExportData, exportFullDataToExcel } from '../helper/GenerateEmbarkExcel.js';
import EmbarkFlightGroup from './embark/EmbarkFlightGroup.vue';

export default {
  components: {
    EmbarkFlightGroup
  },
  data() {
    return {
      embarkations: [],
      checkedCounts: {},
      searchQuery: '',
      inputDate: null,
      loading: false,
      latestUpdatedAt: null,
      currentUser: null,
      expandedGroups: {}

    };
  },
  computed: {
    cruiseLineGroupedData() {
      const groupedByCruiseLine = {};

      // Sort embarkations by date and time
      const sorted = [...this.embarkations].sort((a, b) => {
        const dateA = a.attributes.arrivalDate;
        const timeA = a.attributes.arrivalTime;
        const dateB = b.attributes.arrivalDate;
        const timeB = b.attributes.arrivalTime;

        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        if (timeA < timeB) return -1;
        if (timeA > timeB) return 1;
        return 0;
      });

      sorted.forEach(item => {
        item.checked = item.attributes.guestStatus === 'Checked';

        const cruiseLine = item.attributes.cruiseLine || 'Unknown Cruise Line';
        const flightKey = `${item.attributes.flightNumber}-${item.attributes.arrivalDate}`;

        if (!groupedByCruiseLine[cruiseLine]) {
          groupedByCruiseLine[cruiseLine] = {};
        }

        if (!groupedByCruiseLine[cruiseLine][flightKey]) {
          groupedByCruiseLine[cruiseLine][flightKey] = {
            data: [],
            ownAirCount: 0
          };
        }

        groupedByCruiseLine[cruiseLine][flightKey].data.push(item);

        if (item.attributes.note === 'OWN AIR') {
          groupedByCruiseLine[cruiseLine][flightKey].ownAirCount++;
        }
      });

      return groupedByCruiseLine;
    },
    cruiseLineTotalGuests() {
    const totals = {};
    for (const cruiseLine in this.cruiseLineGroupedData) {
      const flightGroups = this.cruiseLineGroupedData[cruiseLine];
      totals[cruiseLine] = Object.values(flightGroups).reduce(
        (total, group) => total + group.data.length,
        0
      );
    }
    return totals;
  },

    filteredData() {
      if (!this.searchQuery) return this.cruiseLineGroupedData;

      const query = this.searchQuery.toLowerCase();
      const filteredGrouped = {};

      for (const cruiseLine in this.cruiseLineGroupedData) {
        const flightGroups = this.cruiseLineGroupedData[cruiseLine];
        const filteredFlights = {};

        for (const flightKey in flightGroups) {
          const group = flightGroups[flightKey].data.filter(item => {
            const { bookingNumber, firstName, guestsLastName, cabinNumber, flightNumber } = item.attributes;
            return [bookingNumber, firstName, guestsLastName, cabinNumber, flightNumber].some(field =>
              field?.toLowerCase().includes(query)
            );
          });

          if (group.length) {
            filteredFlights[flightKey] = {
              data: group,
              ownAirCount: group.filter(g => g.attributes.note === 'OWN AIR').length
            };
          }
        }

        if (Object.keys(filteredFlights).length > 0) {
          filteredGrouped[cruiseLine] = filteredFlights;
        }
      }

      return filteredGrouped;
    },
    totalGuests() {
      return this.embarkations.length;
    },

    totalCheckedGuests() {
      return this.embarkations.filter(item => item.checked).length;
    },
    totalOwnAir() {
      return this.embarkations.filter(item => item.attributes.note === 'OWN AIR').length;
    },
    totalRemarks() {
      const result = {};
      for (const key in this.groupedData) {
        const group = this.groupedData[key];
        result[key] = group.data.reduce((count, item) => {
          const remark = item.attributes.remarks;
          return remark && remark.trim() !== '' ? count + 1 : count;
        }, 0);
      }
      return result;
    }
  },
  created() {
    this.processFlightStatus = this.processFlightStatus.bind(this);
  },

  methods: {

    formatDateTime(datetime) {
      const options = {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      };
      return new Date(datetime).toLocaleString(undefined, options);
    },
    exportData() {
      prepareAndExportData(this.filteredData);
    },
    exportFullData() {
      const dateStr = this.inputDate ? moment(this.inputDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
      exportFullDataToExcel(this.embarkations, dateStr, 'Embark');
    },

    updateCheckedCount(group) {
      const key = `${group[0].attributes.flightNumber}-${group[0].attributes.arrivalDate}`;
      this.checkedCounts[key] = group.filter(item => item.checked).length;
    },

    async fetchRealTimeData() {
      // Filter out flights that are already landed
      const filteredEmbarkations = this.embarkations.filter(e => e.attributes.status !== 'L' && e.attributes.status !== 'C');
      const uniqueFlightNumbers = [...new Set(filteredEmbarkations.map(e => e.attributes.flightNumber))];

      const fetchFlightData = (carrierFsCode, flightNumber, arrivalDate) => {
        return new Promise((resolve, reject) => {
          const arrivalMoment = moment(arrivalDate);
          const [arrivalYear, arrivalMonth, arrivalDay] = [
            arrivalMoment.year(),
            arrivalMoment.month() + 1, // months are 0-based in moment.js
            arrivalMoment.date()
          ];
          const url = `https://api.flightstats.com/flex/flightstatus/rest/v2/jsonp/flight/status/${carrierFsCode}/${flightNumber}/arr/${arrivalYear}/${arrivalMonth}/${arrivalDay}`;
          const params = {
            appId: process.env.VUE_APP_APP_ID,
            appKey: process.env.VUE_APP_APP_KEY,
            utc: true,
            airport: 'HAM',
            codeType: 'FS',
            callback: `flightStatusCallback_${carrierFsCode}_${flightNumber}`
          };

          // Define the callback function in the global context
          window[`flightStatusCallback_${carrierFsCode}_${flightNumber}`] = (data) => {
            resolve(data);
            // Ensure the context of 'this' is correct
            this.processFlightStatus(data);
            delete window[`flightStatusCallback_${carrierFsCode}_${flightNumber}`];
          };

          const script = document.createElement('script');
          script.src = `${url}?${new URLSearchParams(params).toString()}`;
          script.onload = () => document.head.removeChild(script);
          script.onerror = (error) => {
            console.error(`Error loading JSONP script for flight ${carrierFsCode}${flightNumber}:`, error);
            document.head.removeChild(script);
            reject(error);
          };
          document.head.appendChild(script);
        });
      };

      try {
        const allFlightsData = await Promise.all(uniqueFlightNumbers.map(flightNumber => {
          let carrierFsCode = '';
          let flightNum = '';

          // Check if the flight number starts with one alphabet followed by digits
          if (/^[A-Za-z]\d/.test(flightNumber)) {
            const match = flightNumber.match(/^([A-Za-z]\d)(\d+)/); // One alphabet followed by one digit, then rest digits
            carrierFsCode = match ? match[1] : ''; // First alphabet and one digit
            flightNum = match ? match[2] : ''; // Remaining digits
          } else {
            // Default logic: capture one or more alphabets followed by digits
            const match = flightNumber.match(/^([A-Za-z]+)(\d+)/); // One or more alphabets
            carrierFsCode = match ? match[1] : ''; // Alphabets (up to three)
            flightNum = match ? match[2] : ''; // Remaining digits
          }

          const embarkation = filteredEmbarkations.find(e => e.attributes.flightNumber === flightNumber);
          const arrivalDate = embarkation ? embarkation.attributes.arrivalDate : this.inputDate;

          return fetchFlightData(carrierFsCode, flightNum, arrivalDate);
        }));

        console.log('All Flight Status Data:', allFlightsData);
      } catch (error) {
        console.error('Failed to fetch flight data:', error);
      }


    },

    async processFlightStatus(data) {
  if (data.error) {
    console.error('Error fetching flight status data:', data.error);
    return;
  }

  // Process flightStatuses data
  for (const embarkation of this.embarkations) {
    const matchingFlight = data.flightStatuses.find(flight =>
      (flight.carrierFsCode + flight.flightNumber === embarkation.attributes.flightNumber ||
        flight.codeshares.some(codeshare =>
          codeshare.fsCode + codeshare.flightNumber === embarkation.attributes.flightNumber
        )) &&
      moment(flight.arrivalDate.dateLocal).format('YYYY-MM-DD') === moment(embarkation.attributes.arrivalDate).format('YYYY-MM-DD')
    );

    if (matchingFlight) {
      embarkation.realTimeData = {
        status: matchingFlight.status,
        airportResources: matchingFlight.airportResources || {},
        delays: matchingFlight.delays || {},
        operationalTimes: matchingFlight.operationalTimes || {},
        codeshares: matchingFlight.codeshares || [],
        departureAirport: matchingFlight.departureAirportFsCode || ''
      };

          // Departure airport is missing in case of Oceania
      if (!embarkation.attributes.departureAirport && matchingFlight.departureAirportFsCode) {
        embarkation.attributes.departureAirport = matchingFlight.departureAirportFsCode;
      }

      // Save updated embarkation data to API
      await this.updateEmbarkation(embarkation);
    }
  }
},
    async updateEmbarkation(embarkation) {
      const formattedDate = moment(this.inputDate).format('YYYY-MM-DD');
      const embarkationId = embarkation.id;

      const dataToUpdate = {
        status: embarkation.realTimeData?.status || '',
        departureGateDelayMinutes: embarkation.realTimeData?.delays?.departureGateDelayMinutes || 0,
        arrivalGateDelayMinutes: embarkation.realTimeData?.delays?.arrivalGateDelayMinutes || 0,
        arrivalTerminal: embarkation.realTimeData?.airportResources?.arrivalTerminal || '',
        baggage: embarkation.realTimeData?.airportResources?.baggage || '',
        codeShare: embarkation.realTimeData?.codeshares
          ? embarkation.realTimeData.codeshares.map(codeshare => `${codeshare.fsCode}${codeshare.flightNumber}`).join(', ')
          : '',
          departureAirport: embarkation.attributes.departureAirport || ''
      };
      if (!embarkation.realTimeData?.airportResources?.arrivalTerminal) {
        delete dataToUpdate.arrivalTerminal;
      }

      if (!embarkation.realTimeData?.airportResources?.baggage) {
        delete dataToUpdate.baggage;
      }

      const actualRunwayDeparture = embarkation.realTimeData?.operationalTimes?.actualRunwayDeparture?.dateUtc
        || embarkation.realTimeData?.operationalTimes?.actualGateDeparture?.dateUtc;
      if (actualRunwayDeparture) {
        dataToUpdate.actualRunwayDeparture = actualRunwayDeparture;
      }

      const estimatedRunwayArrival = embarkation.realTimeData?.operationalTimes?.estimatedRunwayArrival?.dateUtc
        || embarkation.realTimeData?.operationalTimes?.estimatedGateArrival?.dateUtc;
      if (estimatedRunwayArrival) {
        dataToUpdate.estimatedRunwayArrival = estimatedRunwayArrival;
      }

      const actualGateArrival = embarkation.realTimeData?.operationalTimes?.actualGateArrival?.dateUtc;
      if (actualGateArrival) {
        dataToUpdate.actualGateArrival = actualGateArrival;
      }
      const estimatedGateArrival = embarkation.realTimeData?.operationalTimes?.estimatedGateArrival?.dateUtc;
      if (estimatedGateArrival) {
        dataToUpdate.estimatedGateArrival = estimatedGateArrival;
      }
      this.loading = true;

      try {
        await this.$xhr.put(`/api/transfers/${embarkationId}`, { data: dataToUpdate });
      } catch (error) {
        console.error('Error updating embarkation:', error);
      }
      finally {
        this.loading = false; // Stop loading
      }
    },
    async refreshData() {
      await this.fetchRealTimeData();
      await this.fetchEmbarkations()
    },
    async fetchEmbarkations() {
      if (!this.inputDate) return;

      const formattedDate = moment(this.inputDate).format('YYYY-MM-DD');

      // Fetch current user if not already available
      const currentUser = this.currentUser || (await this.getCurrentUser());
      if (!currentUser) {
        console.warn('No user data found');
        return;
      }

      try {
        // 1. Get ShipCall ID
        const shipCallRes = await this.$xhr.get(`/api/ship-calls?filters[operationDate][$eq]=${formattedDate}&filters[operationType][$eq]=embarkation&populate=assignedUsers`);
        if (!shipCallRes.data.data || shipCallRes.data.data.length === 0) {
           this.embarkations = [];
           this.latestUpdatedAt = null;
           this.checkedCounts = {};
           return;
        }
        
        const shipCall = shipCallRes.data.data[0];
        
        // 2. Check Permissions (Skip if Authenticated Role, otherwise check assignedUsers)
        const isAuthenticated = currentUser.role?.name === 'Authenticated';
        const isAssigned = shipCall.attributes.assignedUsers?.data?.some(u => u.id === currentUser.id);
        
        if (!isAuthenticated && !isAssigned) {
          console.log(`User does not have access to this ShipCall`);
          this.embarkations = [];
          this.latestUpdatedAt = null;
          this.checkedCounts = {};
          this.showToast('You do not have access to this collection', 'danger');
          return;
        }

        // 3. Fetch Transfers for this ShipCall
        const query = [
          `filters[shipCall][id][$eq]=${shipCall.id}`,
          `pagination[pageSize]=2000`,
          `populate[shipCall][fields][0]=id`
        ].join('&');
        const response = await this.$xhr.get(`/api/transfers?${query}`);
        this.embarkations = response.data.data;

        if (this.embarkations.length > 0) {
          this.latestUpdatedAt = this.embarkations[0].attributes.updatedAt;
        }

        this.checkedCounts = {}; // Clear existing checkedCounts
        this.embarkations.forEach(item => {
          const key = `${item.attributes.flightNumber}-${item.attributes.arrivalDate}`;
          if (!this.checkedCounts[key]) this.checkedCounts[key] = 0;
          if (item.attributes.guestStatus === 'Checked') this.checkedCounts[key]++;
        });
      } catch (error) {
        console.error('Failed to fetch embarkations:', error.response?.data || error.message);
        this.showToast('Failed to load embarkation data', 'danger');
        this.embarkations = [];
      }
    },
    async getCurrentUser() {
      try {
        const response = await this.$xhr.get('/api/users/me?populate=*');
        this.currentUser = response.data;
        return this.currentUser;
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        this.showToast('Failed to load user data', 'danger');
        return null;
      }
    },
    showToast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Success' : 'Error',
        variant: variant,
        solid: true
      });
    },


    handleDateChange(newDate) {
      this.inputDate = newDate;
      this.fetchEmbarkations();
    },


  }
};
</script>

<style scoped>
@import "~@fortawesome/fontawesome-free/css/all.min.css";
@import "../assets/styles/flight-operations.css";
</style>
